// angular
import { DatePipe, NgClass, NgOptimizedImage } from '@angular/common';
import {Component, effect, inject, input, OnInit, signal} from "@angular/core";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// services
import { UserService } from '@app/shared/services/user.service';
// interfaces & env
import { IMessage } from '@app/+chat/shared/interfaces';
import { environment } from '@environments/environment.prod';
// pipes
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import {II18n} from "@shared/interfaces/i18n.interface";
import {take} from "rxjs";
import {StateManagementService} from "@app/+chat/shared/services/state-management.service";

@Component({
  selector: 'pwj-message-box',
  standalone: true,
  imports: [
    NgOptimizedImage,
    TranslateModule,
    DatePipe,
    ImageSourcePipe,
    NgClass,
  ],
  templateUrl: './message-box.component.html',
  styleUrl: './message-box.component.scss',
})
export class MessageBoxComponent implements OnInit {
  public userLang!: string;
  public currentLang!: string;
  public baseUrl: string = environment.baseUrl.file;
  public isHost!: boolean;
  public messageSenderId!: string;
  public isTranslateMode: boolean = false;
  public translateLang: string | undefined = undefined;

  // inputs
  messageData = input.required<IMessage>();
  messageIndex = input.required<number>();

  // dependencies
  private readonly _userService = inject(UserService);
  private readonly _stateManagementService = inject(StateManagementService);
  private readonly _translateService = inject(TranslateService);

  constructor() {
    // Initialize current lang
    effect(() => {
      this.currentLang = this._stateManagementService.currentChatLanguage();
    });

    effect(() => {
      const message = this.messageData();
      if (!message) return;
      // check translate lang
      this._checkTranslateLang();
      // Check if message has a sender
      if (message.sender?.kid) {
        this.isHost = this._userService.userSignal()?.kid === message.sender?.kid;
        this.messageSenderId = message.sender.kid;
      } else {
        // If no sender, treat as system/host message
        this.isHost = true;
      }
    });
  }

  ngOnInit(): void {
    // get user id & lang
    this._userService.user$.subscribe(user => {
      user?.kid && this.compareIds(user?.kid, this.messageSenderId);
      this.userLang = user.lang;
    });

    // get site current lang
    this._translateService.onLangChange.subscribe(state => {
      this.currentLang = state.lang as keyof II18n;
    });
  }

  public compareIds(hostId: string, messageId: string) {
    // compare ids
    this.isHost = hostId === messageId || !messageId;
  }

  public toggleTranslateMessage() {
    this._checkTranslateLang();
    this.isTranslateMode = !this.isTranslateMode;
  }

  private _checkTranslateLang() {
    const message = this.messageData().message;
    let langs: string[] = [];
    if (Object.keys(message).length > 1){
      langs = Object.keys(message).filter((key) => (key !== this.currentLang));
      this.translateLang = langs[0];
    }else {
      this.translateLang = undefined;
    }
  }
}
